import Vue from 'vue'
import Router from 'vue-router'
import { ToastPlugin, ModalPlugin, VBToggle } from 'bootstrap-vue'
import VueCompositionAPI from '@vue/composition-api'
import VueQuill from 'vue-quill-editor'
import VueSweetalert2 from 'vue-sweetalert2'
import VueToastr2 from 'vue-toastr-2'
import 'vue-toastr-2/dist/vue-toastr-2.min.css'
import ImageResize from 'quill-image-resize'
import { ImageDrop } from 'quill-image-drop-module'
import htmlEditButton from 'quill-html-edit-button'
import DatePicker from 'vue2-datepicker'
import permissionsMixin from './mixins/permissions'
import {
  getDay, getMonth, getYear, compareDates,
} from './services/serviceDates'

import router from './router'
import store from './store'
import App from './App.vue'

// Global Components
import './global-components'

// 3rd party plugins
import '@/libs/portal-vue'
import '@/libs/toastification'

window.toastr = require('toastr')
window.moment = require('moment-timezone')
if (window.moment && window.moment.tz) {
  window.moment.tz.setDefault('Europe/Madrid')
}
globalThis.moment = window.moment

const UploadFile = {
  install() {
    Vue.prototype.$saveFiles = data => {
      if (data && data.length > 0) {
        const formData = new FormData()

        data.forEach((element, index) => {
          if (element && element.file) {
            formData.append(`files-${index}`, element.file)
          }
        })

        Vue.prototype.$http.post(`${Vue.prototype.$groupUrl}/files/save-file`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      }
    }
  },
}

const Export = {
  install() {
    Vue.prototype.$export = data => {
      Vue.prototype.$http.post(data.url, data.filters, {
        responseType: 'arraybuffer',
      }).then(response => {
        store.dispatch('modals/toggleExportLoading', false)

        if (data.background) {
          if (response.status == 200) {
            Vue.swal('Export started', 'You will receive an email when its done', 'success')
          } else {
            Vue.swal('Export failed', 'Please, contact to the administrator', 'error')
          }
        } else {
          // Determine content type and filename from headers (fallbacks below)
          const headers = response.headers || {}
          const contentType = headers['content-type'] || ''
          const disposition = headers['content-disposition'] || ''

          let filename = data.name || 'download'

          if (disposition) {
            // Try to extract filename from Content-Disposition
            const fileNameMatch = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)|filename="?([^";]+)"?/) 
            if (fileNameMatch) {
              filename = decodeURIComponent((fileNameMatch[1] || fileNameMatch[2]).trim())
            }
          }

          // If filename doesn't have an extension, pick one from content type
          const hasExt = /\.[a-zA-Z0-9]+$/.test(filename)
          if (!hasExt) {
            if (contentType.includes('spreadsheet') || contentType.includes('excel')) filename = `${filename}.xlsx`
            else if (contentType.includes('zip')) filename = `${filename}.zip`
            else if (contentType.includes('csv')) filename = `${filename}.csv`
            else if (contentType.includes('pdf')) filename = `${filename}.pdf`
          }

          // If content-type is missing or ambiguous, detect ZIP by magic bytes (PK..)
          let finalContentType = contentType || ''
          if (!finalContentType || finalContentType === 'application/octet-stream') {
            try {
              const arr = new Uint8Array(response.data)
              if (arr.length >= 4 && arr[0] === 0x50 && arr[1] === 0x4B && arr[2] === 0x03 && arr[3] === 0x04) {
                finalContentType = 'application/zip'
                if (!filename.endsWith('.zip') && !filename.endsWith('.xlsx')) filename = `${filename}.zip`
              }
            } catch (e) {
              // ignore detection errors and fall back to provided/guessed filename
            }
          }

          // Create blob with the detected content type
          const blobData = new Blob([response.data], { type: finalContentType || 'application/octet-stream' })

          // Create a URL for the blob data and initiate the download using the chosen filename
          const blobUrl = window.URL.createObjectURL(blobData)
          const link = document.createElement('a')
          link.href = blobUrl
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(blobUrl)
        }
      }).catch(() => {
        store.dispatch('modals/toggleExportLoading', false)
        Vue.swal('Export failed', 'Please, contact to the administrator', 'error')
      })
    }
  },
}

const { Quill } = VueQuill
Quill.prototype.getHtml = e => e.container.firstChild.innerHTML
const Block = Quill.import('blots/block')
Block.tagName = 'div'
Quill.register(Block)

// Resize image module
Quill.register('modules/imageResize', ImageResize)

// Image drop module
Quill.register('modules/imageDrop', ImageDrop)

// Show html button
Quill.register('modules/htmlEditButton', htmlEditButton)

Vue.use(UploadFile)
Vue.use(Export)
// BSV Plugin Registration
Vue.use(ToastPlugin)
Vue.use(VueToastr2)
Vue.use(ModalPlugin)

Vue.use(DatePicker)

Vue.use(getDay)
Vue.use(getMonth)
Vue.use(getYear)
Vue.use(compareDates)
Vue.mixin(permissionsMixin)
Vue.use(VueQuill, {
  modules: {
    clipboard: {
      matchVisual: false, // Reté el format al màxim
    },
    htmlEditButton: {},
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'], // toggled buttons
      [{ header: 1 }, { header: 2 }, { header: 3 }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video', 'formula'],
      [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
      [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
      [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
      [{ direction: 'rtl' }], // text direction
      [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }], // dropdown with defaults from theme
      [{ font: [] }],
      [{ align: [] }],
      ['clean'],
    ],
    imageResize: {
      displayStyles: {
        backgroundColor: 'black',
        border: 'none',
        color: 'white',
      },
      modules: ['Resize', 'DisplaySize', 'Toolbar'],
    },
  },
})
Vue.use(VueSweetalert2)
Vue.directive('b-toggle', VBToggle)
// Composition API
Vue.use(VueCompositionAPI)

Vue.config.productionTip = false

// Create a copy of the original resolve function
const originalResolve = Router.prototype.resolve

// Parameters to preserve from current route to the new route
const PRESERVE_PARAMS = ['actAs', 'prefilterUser']

// Override the resolve function
Router.prototype.resolve = function (to, current, append) {
  // Get the current route from the router instance if not provided
  const currentRoute = current || this.currentRoute

  // First get the resolved route using the original resolve method
  const resolved = originalResolve.call(this, to, current, append)

  // Only proceed if we have a current route with query parameters
  if (currentRoute && currentRoute.query) {
    // Create a new location object based on the resolved one
    const newLocation = {
      ...resolved.location,
    }

    // Initialize query if it doesn't exist
    if (!newLocation.query) {
      newLocation.query = {}
    }

    // Check each parameter that should be preserved
    let shouldReResolve = false

    PRESERVE_PARAMS.forEach(param => {
      // Only copy the parameter if it exists in current route and doesn't exist in target route
      if (currentRoute.query[param] && !newLocation.query[param]) {
        newLocation.query[param] = currentRoute.query[param]
        shouldReResolve = true
      }
    })

    // Only re-resolve if we actually added parameters
    if (shouldReResolve) {
      // Re-resolve with the updated query to get the correct href
      return originalResolve.call(this, newLocation, current, append)
    }
  }

  return resolved
}

new Vue({
  router,
  store,
  render: h => h(App),
}).$mount('#app')
